(ns nosco.richtext.uploads
  (:require
   ["prosemirror-model" :as prosemirror-model]
   ["prosemirror-state" :as prosemirror-state]
   ["react" :as React]
   ["react-dom" :as react-dom]
   ["react-dom/client" :as react-dom-client]
   [ajax.core :as ajax]
   [cljs-bean.core :as bean]
   [clojure.set :as set]
   [clojure.string :as string]
   [goog.object :as gobj]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.i18n.translate :as t]
   [nosco.icons2 :as icons2]
   [nosco.richtext.embed :as embed]
   [nosco.richtext.file-uploads :as file-uploads]
   [nosco.richtext.image-uploads :as image-uploads]
   [nosco.richtext.schema :as schema]
   [nosco.richtext.util :as richtext-util]
   [nosco.richtext.video-uploads :as video-uploads]
   [nosco.ui.base :as base]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.inputs :as inputs]
   [nosco.ui.modals3 :as modals3]
   [nosco.ui.popup :as popup]
   [nosco.ui.tabs2 :as tabs2]
   [nosco.ui.text :as text]
   [nosco.ui.tooltips :as tooltips]))

(def ^:private portrait-image-initial-max-height 500)

(defn image-initial-dimensions
  "Returns a vector of [width height] specifying the initial dimensions an
  image should be set to in the editor based on its real width and height.
  For portrait images, a max-height is defined which requires the image to
  be scaled to fit. Returns nil when the image should not have any dimensions
  set."
  [width height]
  (when (> height width portrait-image-initial-max-height)
    [(Math/round (* (/ portrait-image-initial-max-height height) width))
     portrait-image-initial-max-height]))

(defn- get-image-dimensions [url]
  (js/Promise.
   (fn [resolve reject]
     (let [img (js/Image.)]
       (.addEventListener img "load" (fn [] (resolve [(.-width img) (.-height img)])))
       (.addEventListener img "error" (fn [event] (reject event)))
       (set! (.-src img) url)))))

(defn files->form-data [files]
  (let [form-data (js/FormData.)]
    (if (sequential? files)
      (doseq [f files]
        (.append form-data "file" f))
      (doseq [file-key (.keys js/Object files)]
        (.append form-data "file" (aget files file-key))))
    form-data))

;; TODO Use the upload logic from nosco.richtext.image-uploads
(defn upload-images
  "POST the files from the FileList to /images and return a Promise that
  contains an image URL. "
  [files]
  (let [form-data (files->form-data files)]
    (js/Promise.
     (fn [resolve reject]
       ;; TODO /images only accepts one file, enforce that
       ;; TODO error handler?
       (ajax/POST "/images"
         {:body form-data
          :response-format (ajax/json-response-format {:keywords? true})
          :handler (fn [resp]
                     (if (:error resp)
                       (reject (:error resp))
                       (resolve (:filelink resp))))
          :error-handler reject})))))

(defn upload-files
  "POST the files from the FileList to /files and return a Promise that
  contains a JS map containing :id, :url and :filename keys."
  [files]
  (let [form-data (files->form-data files)]
    (js/Promise.
     (fn [resolve reject]
       ;; TODO /images only accepts one file, enforce that
       ;; TODO error handler?
       (ajax/POST "/files"
         {:body form-data
          :response-format (ajax/json-response-format {:keywords? true})
          :headers {:x-request-origin "prosemirror"}
          :handler (fn [resp]
                     (if (:error resp)
                       (reject (:error resp))
                       (let [resp (first resp)
                             file-info (val resp)]
                         (resolve
                          #js {:url (:filelink file-info)
                               :id (name (key resp))
                               :name (:name file-info)}))))
          :error-handler reject})))))

(defn upload-videos [files]
  (js/Promise.
   (fn [resolve reject]
     (let [upload-state (atom {})
           dispatch (fn [action]
                      (swap! upload-state video-uploads/video-upload-state-reducer action)
                      (let [video-state (first (vals @upload-state))]
                        (cond
                          (:uploaded? video-state)
                          (resolve (:embed-html video-state))

                          (:error video-state)
                          (reject (:error video-state)))))]
       (video-uploads/upload-video {:file (first files)
                                    :dispatch dispatch})))))

(defn- insert-uploaded-image! [^js view url {:keys [width height]}]
  (let [selection (.. view -state -selection)
        ;; Get the position after the current first-level node
        pos (.. selection -$to (after 1))
        $pos (.. view -state -doc (resolve pos))
        image-node (.. view -state -schema -nodes -image
                       (create #js {:src url
                                    :width width
                                    :height height}))
        figure-node (.. view -state -schema -nodes -figure
                        (create #js {:data-align "center"}
                                image-node))
        fragment (richtext-util/figure-insertion-fragment view figure-node pos)
        tr (.. view -state -tr (insert pos fragment))]
    (.dispatch view tr)
    (.focus view)))

(defnc ImageUploadPopup [{:keys [view showing on-close]}]
  (let [[upload-state upload-dispatch reset-upload-state] (image-uploads/use-image-upload-reducer)
        can-insert (and (not-empty (:uploads upload-state))
                        (not (image-uploads/uploads-in-progress? upload-state)))
        on-close*
        (fn []
          (reset-upload-state)
          (on-close))]
    [modals3/ModalDialog
     {:showing showing
      :on-close on-close*
      :title (t/t "Add images")
      :cancel-label (t/t "Cancel")
      :confirm-label (t/t "Insert")
      :confirm-props {:disabled (not can-insert)}
      :on-confirm
      (fn []
        (doseq [[_ {:keys [url]}] (:uploads upload-state)]
          (-> (get-image-dimensions url)
              (.then
               (fn [[width height]]
                 (let [[width' height'] (image-initial-dimensions width height)]
                   (insert-uploaded-image! view url {:width width' :height height'}))))))
        (on-close*))}
     [image-uploads/ImageUpload
      {:state upload-state
       :dispatch upload-dispatch}]]))

(defn- insert-uploaded-file! [^js view href file-id name]
  (let [nodes (.. view -state -schema -nodes)
        file-link ^js/Object (.-file_link nodes)
        file-node (.. file-link (create #js {:href href
                                             :file file-id
                                             :name name}))
        fragment (.. prosemirror-model/Fragment (from file-node)
                     ;; Insert space after so it's possible to place
                     ;; cursor between file links
                     (addToEnd (.. view -state -schema (text " "))))
        transaction (.. view -state -tr)
        transaction (.replaceSelectionWith transaction fragment false)]
    (.dispatch view transaction)
    (.focus view)))

(defnc FileUploadPopup [{:keys [view showing on-close]}]
  (let [[upload-state upload-dispatch reset-upload-state] (file-uploads/use-file-upload-reducer)
        can-insert (and (not-empty (:uploads upload-state))
                        (not (file-uploads/uploads-in-progress? upload-state)))
        on-close*
        (fn []
          (reset-upload-state)
          (on-close))]
    [modals3/ModalDialog
     {:showing showing
      :on-close on-close*
      :title (t/t "Add files")
      :cancel-label (t/t "Cancel")
      :confirm-label (t/t "Insert")
      :confirm-props {:disabled (not can-insert)}
      :on-confirm
      (fn []
        (doseq [[_ {:keys [href file-id filename]}] (:uploads upload-state)]
          (insert-uploaded-file! view href file-id filename))
        (on-close*))}
     [file-uploads/FileUpload
      {:state upload-state
       :dispatch upload-dispatch}]]))

(defn create-video-node [view embed-html]
  (when-let [attrs (schema/iframe-info (schema/iframe-node embed-html))]
    (let [ratio (:ratio attrs)
          attrs (select-keys attrs schema/iframe-attrs)
          iframe-node (.. view -state -schema (node "iframe" (clj->js attrs)))
          figure-node (.. view -state -schema (node "figure"
                                                    #js {:data-src (:src attrs)
                                                         :data-ratio ratio
                                                         :class "nosco-iframe-container"
                                                         :style (str "--figure-ratio: " ratio "%;")}
                                                    iframe-node))]
      figure-node)))

(defn- insert-uploaded-video! [^js view embed-html]
  (when-let [figure-node (create-video-node view embed-html)]
    (let [selection (.. view -state -selection)
          insert-pos (.. selection -$anchor (after 1))
          _ (richtext-util/maybe-insert-empty-paragraph! view insert-pos)
          tr (.. view -state -tr
                 (insert insert-pos
                         (richtext-util/figure-insertion-fragment
                          view
                          figure-node
                          insert-pos)))]
      (.dispatch view tr)))
  (.focus view))

(defnc VideoUploadPopup [{:keys [view showing on-close]}]
  (let [can-upload (video-uploads/can-upload?)
        default-tab-fn (fn [] (if can-upload :upload :embed))
        [selected-tab ->selected-tab] (hooks/useState default-tab-fn)
        [embed-code-field-state ->embed-code-field-state] (hooks/useState embed/embed-code-field-initial-state)
        [video-upload-state dispatch] (video-uploads/use-video-upload-state)]
    (hooks/useEffect
     (fn []
       (->selected-tab (default-tab-fn))
       (->embed-code-field-state embed/embed-code-field-initial-state))
     [showing])
    [modals3/ModalDialog
     {:showing showing
      :on-close on-close
      :title (t/t "Add videos")
      :cancel-label (t/t "Cancel")
      :confirm-label (case selected-tab
                       :upload (t/t "Insert")
                       :embed (t/t "Embed"))
      :confirm-props {:disabled (and (= :upload selected-tab)
                                     (or
                                      (empty? video-upload-state)
                                      (some (fn [[_ upload]]
                                              (video-uploads/uploading? upload)) video-upload-state)))}
      :on-confirm (fn []
                    (case selected-tab
                      :upload
                      (do
                        (doseq [[_ {:keys [embed-html]}] video-upload-state]
                          (insert-uploaded-video! view embed-html))
                        (on-close))

                      :embed
                      (do
                        (embed/insert-embed-code-field! view embed-code-field-state)
                        (on-close))))}
     (when can-upload
       [:div {:class ["mb-spacing-32"]}
        [tabs2/TabGroup
         {:selected-tab selected-tab
          :on-select #(->selected-tab %)
          :variant :green
          :background-variant :white
          :tabs [{:key :upload :label (t/t "Upload")}
                 {:key :embed :label (t/t "Embed")}]}]])
     (case selected-tab
       :upload
       [:div
        [text/Text {:variant text/Body-Md-Regular}
         (t/t "You can upload common video files like MP4, MOV, WMV, AVI, and more.")]
        [:div {:class ["mt-spacing-16"]}
         [video-uploads/VideoUpload {:state video-upload-state
                                     :dispatch dispatch}]]]
       :embed
       [embed/EmbedCodeField
        {:state embed-code-field-state
         :on-state-change #(->embed-code-field-state %)}])]))

(defn- render-as-portal [dom el]
  (let [container (.createElement js/document "div")
        root (react-dom-client/createRoot container)
        portal (react-dom/createPortal el dom)]
    (.setAttribute container "id" (name (gensym "nosco-prosemirror-react-container")))
    (.. js/document -body (appendChild container))
    (.render root portal)
    (fn []
      ;; Use setTimeout so that the root is unmounted asynchronously. Prevents React complaining
      (js/setTimeout
       (fn []
         (.unmount root)
         (.. js/document -body (removeChild container)))))))

(def minimum-image-size 150)

(defn- resize-image-dimensions [[width height] width-delta]
  (let [width' (max (+ width width-delta) minimum-image-size)
        height' (Math/round (* (/ width' width) height))
        width' (if (< height' minimum-image-size)
                 (* width' (/ minimum-image-size height'))
                 width')
        height' (max height' minimum-image-size)]
    [width' height']))

(defnc ^:private ImageNodeView [{:keys [modal-manager-ref
                                        overlay-container-node
                                        node
                                        caption
                                        on-save-caption
                                        alignment
                                        on-align
                                        on-resize
                                        on-remove
                                        on-select]}]
  (let [[width ->width] (hooks/useState nil)
        [height ->height] (hooks/useState nil)
        [resizing ->resizing] (hooks/useState nil)
        [editing-caption ->editing-caption] (hooks/useState nil)
        outer-ref (React/useRef)
        image-ref (React/useRef)]
    (hooks/useEffect
     (fn []
       (when resizing
         (let [mouse-move-fn
               (fn [^js event]
                 (when (.-current image-ref)
                   (let [center-aligned (= "center" (.. image-ref -current (closest "figure") -dataset -align))
                         width-delta (cond-> (- (:initial-x resizing) (.-clientX event))
                                       center-aligned (* 2)
                                       (= :right (:side resizing)) (-))
                         [width' height'] (resize-image-dimensions (:initial-size resizing) width-delta)]
                     (->width width')
                     (->height height'))))
               mouse-up-fn
               (fn []
                 (when (and (.-current outer-ref) (.-current image-ref))
                   (let [max-width (.. outer-ref -current -offsetWidth)
                         img-width (.. image-ref -current -offsetWidth)]
                     (.removeEventListener js/window "mousemove" mouse-move-fn)
                     (if (< img-width max-width)
                       (on-resize [img-width (.. image-ref -current -offsetHeight)])
                       ;; Reset to full-width
                       (on-resize nil)))))]
           (.addEventListener js/window "mousemove" mouse-move-fn)
           (.addEventListener js/window "mouseup" mouse-up-fn)
           (fn []
             (.removeEventListener js/window "mousemove" mouse-move-fn)
             (.removeEventListener js/window "mouseup" mouse-up-fn)))))
     [resizing])
    [:provider {:context base/ModalManagerContext
                :value modal-manager-ref}
     [:provider {:context popup/OverlayContainerContext
                 :value overlay-container-node}
      [:div {:ref outer-ref :style {:height "auto"}}
       [:div {:class ["nosco-richtext-editor-image"]}
        [:img
         (cond-> (bean/bean (.-attrs ^js node))
           :always (assoc :ref image-ref)
           width (assoc :width width)
           height (assoc :height height))]
        [:div {:class ["nosco-richtext-editor-image-toolbar"]}
         [:div {:class ["nosco-richtext-editor-image-toolbar__inner"]}
          [:div {:class ["nosco-richtext-editor-image-toolbar__group"]}
           [tooltips/WithTooltip {:label (t/t "Align left")}
            [buttons2/Button
             {:variant :icon-light
              :icon icons2/Image-Left-small
              :activated (= "left" alignment)
              :on-click #(on-align "left")}]]
           [tooltips/WithTooltip {:label (t/t "Align center")}
            [buttons2/Button
             {:variant :icon-light
              :icon icons2/Image-Center-small
              :activated (= "center" alignment)
              :on-click #(on-align "center")}]]
           [tooltips/WithTooltip {:label (t/t "Align right")}
            [buttons2/Button
             {:variant :icon-light
              :icon icons2/Image-Right-small
              :activated (= "right" alignment)
              :on-click #(on-align "right")}]]]
          [:div {:class ["nosco-richtext-editor-image-toolbar__group"]}
           [tooltips/WithTooltip {:label (t/t "Image caption")}
            [buttons2/Button
             {:variant :icon-light
              :icon icons2/Image-Caption-small
              :on-click #(->editing-caption {:caption caption})}]]]
          [:div {:class ["nosco-richtext-editor-image-toolbar__group"]}
           [tooltips/WithTooltip {:label (t/t "Remove image")}
            [buttons2/Button
             {:variant :icon-light
              :icon icons2/Delete-small
              :on-click on-remove}]]]]]
        [:div {:class ["nosco-richtext-editor-image-resize"]}
         [:div
          {:class ["nosco-richtext-editor-image-resize-handle"]
           :on-mouse-down
           (fn [^js event]
             (when (= 0 (.-button event))
               (.preventDefault event)
               (on-select)
               (->resizing {:initial-x (.-clientX event)
                            :initial-size [(.. image-ref -current -offsetWidth)
                                           (.. image-ref -current -offsetHeight)]
                            :side :left})))}
          [:div {:class ["nosco-richtext-editor-image-resize-handle__bar"]}]]
         [:div
          {:class ["nosco-richtext-editor-image-resize-handle"]
           :on-mouse-down
           (fn [^js event]
             (when (= 0 (.-button event))
               (.preventDefault event)
               (on-select)
               (->resizing {:initial-x (.-clientX event)
                            :initial-size [(.. image-ref -current -offsetWidth)
                                           (.. image-ref -current -offsetHeight)]
                            :side :right})))}
          [:div {:class ["nosco-richtext-editor-image-resize-handle__bar"]}]]]
        [modals3/ModalDialog
         {:showing (boolean editing-caption)
          :size :narrow
          :title (t/t "Image caption")
          :cancel-label (t/t "Cancel")
          :confirm-label (t/t "Save")
          :on-close #(->editing-caption nil)
          :on-confirm
          (fn []
            (on-save-caption (:caption editing-caption))
            (->editing-caption nil))}
         [inputs/TextArea {:value (:caption editing-caption) :set-value #(->editing-caption assoc :caption %)}]]]]]
     (when caption
       [:figcaption caption])]))

(defnc ^:private IframeNodeView [{:keys [node on-remove]}]
  [:<>
   [:iframe
    (-> (bean/bean (.-attrs ^js node))
        (set/rename-keys {:srcdoc :srcDoc
                          :referrerpolicy :referrerPolicy
                          :allowfullscreen :allowFullScreen}))]
   [buttons2/Button
    {:variant :action-secondary
     :icon icons2/Remove-small
     :class ["nosco-richtext-editor-controlled-figure__button"]
     :aria-label (t/t "Remove")
     :on-click on-remove}]])

(defn- make-figure-node-view [{:keys [modal-manager-ref overlay-container-node]} ^js node ^js view get-position-fn]
  (let [delete-figure-fn (fn []
                           (let [resolved-position (.. view -state -doc (resolve (get-position-fn)))
                                 node-selection (prosemirror-state/NodeSelection. resolved-position)
                                 transaction (.. view -state -tr
                                                 (setSelection node-selection)
                                                 (deleteSelection))]
                             (.dispatch view transaction)))
        select-figure-fn (fn []
                           (let [resolved-position (.. view -state -doc (resolve (get-position-fn)))
                                 node-selection (prosemirror-state/NodeSelection. resolved-position)
                                 transaction (.. view -state -tr (setSelection node-selection))]
                             (.dispatch view transaction)))
        child-type (.. node -firstChild -type -name)
        caption-node (when (= "image" child-type) (.maybeChild node 1))
        el (case child-type
             "image" (hx/f [ImageNodeView
                            {:modal-manager-ref modal-manager-ref
                             :overlay-container-node overlay-container-node
                             :node (.-firstChild node)
                             :caption (some-> caption-node (.-textContent))
                             :alignment (gobj/get (.-attrs node) "data-align")
                             :on-remove delete-figure-fn
                             :on-select select-figure-fn
                             :on-align
                             (fn [alignment]
                               (let [transaction (.. view -state -tr
                                                     (setNodeAttribute (get-position-fn) "data-align" alignment))]
                                 (.dispatch view transaction)))
                             :on-resize
                             (fn [[width height]]
                               (let [figure-pos (get-position-fn)
                                     current-figure-node (.. view -state -doc (nodeAt figure-pos))]
                                 (when current-figure-node
                                   (let [image-node-pos (+ figure-pos (.. current-figure-node -firstChild -nodeSize))
                                         transaction (.. view -state -tr
                                                         (setNodeAttribute image-node-pos "width" width)
                                                         (setNodeAttribute image-node-pos "height" height))]
                                     (.dispatch view transaction)))))
                             :on-save-caption
                             (fn [caption]
                               (let [caption-node' (when-not (string/blank? caption)
                                                     (.. view -state -schema -nodes -figcaption
                                                         (create nil (.. view -state -schema (text caption)))))
                                     figure-pos (get-position-fn)
                                     current-figure-node (.. view -state -doc (nodeAt figure-pos))]
                                 (when current-figure-node
                                   (let [caption-pos (+ figure-pos (.. current-figure-node -firstChild -nodeSize) 1)]
                                     (if caption-node
                                       (let [$caption-pos (.. view -state -doc (resolve caption-pos))
                                             caption-sel (prosemirror-state/NodeSelection. $caption-pos)
                                             tr (.. view -state -tr
                                                    (setSelection caption-sel))
                                             tr (if caption-node'
                                                  (.replaceSelectionWith tr caption-node')
                                                  (.deleteSelection tr))]
                                         (.dispatch view tr))
                                       (when caption-node'
                                         (let [tr (.. view -state -tr (insert caption-pos caption-node'))]
                                           (.dispatch view tr))))
                                     (let [after-figure-pos (.. view -state -doc (resolve (inc figure-pos)) (after))
                                           $after-figure-pos (.. view -state -doc (resolve after-figure-pos))
                                           ;; Move selection to the first node after the figure
                                           sel (.findFrom prosemirror-state/Selection $after-figure-pos 1)]
                                       (when sel
                                         (.dispatch view (.. view -state -tr (setSelection sel)))))
                                     (.focus view)))))}])

             "iframe" (hx/f [IframeNodeView
                             {:node (.-firstChild node)
                              :on-remove #(delete-figure-fn)}]))
        dom (.createElement js/document "figure")
        destroy (render-as-portal dom el)]
    (doseq [[k v] (js/Object.entries (.-attrs node))
            :when v]
      (.setAttribute dom k v))
    (.. dom -classList (add "nosco-richtext-editor-controlled-figure"
                            (case child-type
                              "image" "nosco-richtext-editor-controlled-figure--image"
                              "iframe" "nosco-richtext-editor-controlled-figure--iframe")))
    #js {:dom dom
         :destroy destroy}))

(defonce controlled-figure-plugin-key (prosemirror-state/PluginKey. "nosco-uploads-remove-figure"))

(defn controlled-figure-plugin
  "Returns a plugin which adds controls to the figure node type."
  [props]
  (prosemirror-state/Plugin.
   #js {:key controlled-figure-plugin-key
        :props #js {:nodeViews #js {:figure (partial make-figure-node-view props)}}}))
